import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

/**
 * Poll the admin inbox summary while the admin is logged in. Returns the
 * latest counts (all zero until the first fetch resolves) plus a `refresh`
 * function that other admin views can call after they mark items read /
 * delete rows, so the badge updates instantly instead of waiting for the
 * next poll tick.
 */
const DEFAULT_SUMMARY = {
  prayers: 0,
  contacts: 0,
  hall_rentals: 0,
  registrations: 0,
  total: 0,
};

export default function useInboxSummary(isAdmin, intervalMs = 30000) {
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const timer = useRef(null);

  const fetchOnce = async () => {
    try {
      const { data } = await api.inboxSummary();
      setSummary(data);
    } catch { /* silent — auth expired etc. */ }
  };

  useEffect(() => {
    // Reset when logging out so a stale badge doesn't linger.
    if (!isAdmin) {
      setSummary(DEFAULT_SUMMARY);
      return undefined;
    }
    fetchOnce();
    timer.current = setInterval(fetchOnce, intervalMs);
    // Also refresh when the tab regains focus after being backgrounded.
    const onVisible = () => { if (document.visibilityState === 'visible') fetchOnce(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAdmin, intervalMs]);

  return { summary, refresh: fetchOnce };
}
